/**
 * Pure, React-free helpers for the Task Templates domain: id generation, the
 * built-in default template (its ordered {@link DEFAULT_TEMPLATE_SECTIONS} + the
 * deterministic {@link DEFAULT_TEMPLATE_MARKDOWN} skeleton), draft validation,
 * blank/default/edit draft factories, default-seed detection, sorting, and the
 * pointer-row index-attribute mapping used by {@link ./taskTemplateStore}.
 *
 * Keeping the index-attribute mapping (and its inverse) here — rather than
 * inside the store — makes it unit-testable without the API facade, and keeps
 * the store a thin data-access wrapper. Mirrors {@link ../preplanning/prePlanModel}.
 */

import { v4 as uuid } from 'uuid';
import type { StringMap } from '../../api';
import type {
  TaskTemplate,
  TaskTemplateDraft,
  TaskTemplateSection,
  TaskTemplateSummary,
} from './types';

/** Logical collection name for the generic document store. */
export const TASK_TEMPLATES_COLLECTION = 'taskTemplates';

/** Name of the single built-in default template, seeded once when none exist. */
export const DEFAULT_TASK_TEMPLATE_NAME = 'Default Task Template';

/**
 * The ordered sections every task template starts from. This is the canonical
 * source of truth for {@link DEFAULT_TEMPLATE_MARKDOWN}'s headings, so the
 * structured list and the Markdown skeleton can never drift.
 */
export const DEFAULT_TEMPLATE_SECTIONS: readonly TaskTemplateSection[] = [
  { title: 'Goal', hint: 'The outcome this task advances.' },
  { title: 'Deliverables', hint: 'Concrete artifacts produced.' },
  { title: 'Why', hint: 'Why this matters / the motivation.' },
  { title: 'Exit Criteria', hint: 'How we know it is done.' },
  { title: 'Required By', hint: 'Target date (YYYY-MM-DD).' },
  {
    title: 'Tasks',
    hint: 'A checklist; subtasks each carry an effort-in-days figure.',
  },
  { title: 'Status', hint: 'Current status of the work.' },
  { title: 'Total Effort In Days', hint: 'Sum of subtask efforts, in days.' },
] as const;

/**
 * The default template body as Markdown. Uses a heading per section (in the
 * {@link DEFAULT_TEMPLATE_SECTIONS} order), a Tasks checklist containing a
 * sample subtask annotated with `(effort: Nd)`, and a "Total Effort In Days"
 * line. Rendered inline by MarkdownView.
 */
export const DEFAULT_TEMPLATE_MARKDOWN = `# Goal

_Describe the outcome this task advances._

# Deliverables

- Deliverable 1
- Deliverable 2

# Why

_Explain why this matters and the motivation behind it._

# Exit Criteria

- [ ] Criterion 1
- [ ] Criterion 2

# Required By

_Target date (YYYY-MM-DD)._

# Tasks

- [ ] Task 1
    - [ ] Subtask 1.1 (effort: 1d)
    - [ ] Subtask 1.2 (effort: 2d)
- [ ] Task 2
    - [ ] Subtask 2.1 (effort: 1d)

# Status

Not started

# Total Effort In Days

4
`;

/** Deterministic document id for a new task template record. */
export function taskTemplateDocId(): string {
  return `tmpl_${uuid()}`;
}

/**
 * Validates a draft for save. A template needs a name and some Markdown content
 * to be useful.
 */
export function validateTaskTemplateDraft(draft: TaskTemplateDraft): string[] {
  const problems: string[] = [];
  if (!draft.name.trim()) problems.push('A template name is required.');
  if (!draft.content.trim()) {
    problems.push(
      'Markdown content is required — the template renders it inline.',
    );
  }
  return problems;
}

/** Creates an empty draft for the create form (optionally seeding fields). */
export function blankTaskTemplateDraft(
  seed: Partial<TaskTemplateDraft> = {},
): TaskTemplateDraft {
  return {
    name: '',
    isDefault: false,
    content: '',
    sections: [],
    ...seed,
  };
}

/**
 * The draft for the built-in default template. Used to seed the collection the
 * first time it is empty (see {@link needsDefaultSeed}).
 */
export function defaultTaskTemplateDraft(): TaskTemplateDraft {
  return {
    name: DEFAULT_TASK_TEMPLATE_NAME,
    isDefault: true,
    content: DEFAULT_TEMPLATE_MARKDOWN,
    sections: DEFAULT_TEMPLATE_SECTIONS.map((s) => ({ ...s })),
  };
}

/** Projects an existing template into an editable draft. */
export function draftFromTemplate(template: TaskTemplate): TaskTemplateDraft {
  return {
    name: template.name,
    isDefault: template.isDefault ?? false,
    content: template.content,
    sections: (template.sections ?? []).map((s) => ({ ...s })),
  };
}

/** True when the collection has no (non-deleted) templates and needs seeding. */
export function needsDefaultSeed(summaries: TaskTemplateSummary[]): boolean {
  return summaries.length === 0;
}

/**
 * Sorts summaries with the default template first, then by name (A→Z), with the
 * most-recently-updated as a stable tiebreak.
 */
export function sortTaskTemplates(
  items: TaskTemplateSummary[],
): TaskTemplateSummary[] {
  return items
    .slice()
    .sort(
      (a, b) =>
        Number(b.isDefault) - Number(a.isDefault) ||
        a.name.localeCompare(b.name) ||
        (b.lastUpdated ?? '').localeCompare(a.lastUpdated ?? ''),
    );
}

/** Pointer-row index attribute keys (mirrored onto the DDB pointer row). */
export const TASK_TEMPLATE_ROW = {
  primaryIdentifier: 'primaryIdentifier',
  collection: 'collection',
  name: 'name',
  isDefault: 'isDefault',
  createdBy: 'createdBy',
  createdAt: 'createdAt',
  lastUpdated: 'lastUpdated',
  deleted: 'deleted',
} as const;

/**
 * Builds the queryable index attributes mirrored onto the pointer row. The
 * Markdown `content` and the `sections` list are intentionally NOT mirrored
 * (they live in the S3 body); the list view reconstructs summaries from these
 * attributes without hydrating S3.
 */
export function taskTemplateIndexAttributes(template: TaskTemplate): StringMap {
  return {
    [TASK_TEMPLATE_ROW.name]: template.name ?? '',
    [TASK_TEMPLATE_ROW.isDefault]: template.isDefault ? 'true' : 'false',
    [TASK_TEMPLATE_ROW.createdBy]: template.createdBy ?? '',
    [TASK_TEMPLATE_ROW.createdAt]: template.createdAt ?? '',
    [TASK_TEMPLATE_ROW.lastUpdated]: template.lastUpdated ?? '',
    [TASK_TEMPLATE_ROW.deleted]: template.deleted ? 'true' : 'false',
  };
}

/** Reconstructs a task-template summary from a pointer row. */
export function taskTemplateSummaryFromRow(row: StringMap): TaskTemplateSummary {
  return {
    id: row[TASK_TEMPLATE_ROW.primaryIdentifier] ?? '',
    name: row[TASK_TEMPLATE_ROW.name] ?? '',
    isDefault: row[TASK_TEMPLATE_ROW.isDefault] === 'true',
    createdBy: row[TASK_TEMPLATE_ROW.createdBy] ?? '',
    createdAt: row[TASK_TEMPLATE_ROW.createdAt] ?? '',
    lastUpdated: row[TASK_TEMPLATE_ROW.lastUpdated] ?? '',
  };
}
